<template>
  <Teleport to="body">
    <Transition name="fade">
      <div v-if="isOpen" class="fixed inset-0 flex items-center justify-center backdrop-blur-md bg-black/30 z-50" @click.self="$emit('close')">
        <div
          class="flex flex-col md:flex-row items-center justify-center bg-white rounded-lg shadow-lg p-6 gap-6 w-full max-w-xl mx-4 md:mx-0"
          :class="customClass">
          <div class="flex flex-col items-center justify-center gap-3 w-full md:w-1/2">
            <!-- Icono -->
            <div v-if="icon" class="flex justify-center mb-3">
              <component :is="icon" class="w-12 h-12 text-blue-300" />
            </div>

            <!-- Título -->
            <h2 class="text-xl font-semibold text-gray-800 text-center">
              {{ title }}
            </h2>

            <!-- Selector -->
            <div class="relative w-full">
              <select v-model="selectedValue"
                class="mt-4 border border-gray-300 rounded px-3 py-2 w-full text-gray-700 focus:ring focus:ring-blue-200 appearance-none bg-white">
                <option disabled value=""> {{ $t('modal.select_option') }} </option>
                <option v-for="(option, index) in listOptions" :key="index" :value="option.key"
                  class="py-2 px-3 hover:bg-gray-100">
                  {{ option.label }}
                </option>
              </select>
              <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                <svg class="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                  <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/>
                </svg>
              </div>
            </div>

            <!-- Motivo (obligatorio para ciertas opciones, ej. cancelar) -->
            <div v-if="reasonRequired" class="w-full">
              <textarea v-model="reasonText" rows="3"
                :placeholder="reasonLabel || $t('modal.reason_placeholder')"
                class="mt-2 border border-gray-300 rounded px-3 py-2 w-full text-gray-700 focus:ring focus:ring-blue-200 resize-none"></textarea>
            </div>

            <!-- Botones -->
            <div class="mt-4 flex gap-2">
              <ButtonWhite
                @click="handleAction"
                :disabled="!canConfirm"
                :title="preventRedirect ? $t('modal.accept-change') : $t('modal.go-to-request')"
              />
              <ButtonWhite @click="$emit('close')" :title="$t('modal.close')" />
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import ButtonWhite from '../button/ButtonWhite.vue'

const props = defineProps({
  isOpen: Boolean,
  title: String,
  icon: [Object, Function],
  customClass: String,
  listOptions: {
    type: Array,
    default: () => []
  },
  modelValue: [String, Object],
  preventRedirect: {
    type: Boolean,
    default: false
  },
  reasonRequiredValues: {
    type: Array,
    default: () => []
  },
  reasonLabel: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['update:modelValue', 'close', 'confirm'])

const selectedValue = ref(props.modelValue)
const reasonText = ref('')
const router = useRouter()

watch(
  () => props.modelValue,
  (newVal) => {
    selectedValue.value = newVal
  }
)

watch(
  () => props.isOpen,
  (open) => {
    if (!open) {
      reasonText.value = ''
    }
  }
)

const reasonRequired = computed(() => props.reasonRequiredValues.includes(selectedValue.value))
const canConfirm = computed(() => !!selectedValue.value && (!reasonRequired.value || reasonText.value.trim().length > 0))

const handleAction = () => {
  if (selectedValue.value && canConfirm.value) {
    if (props.preventRedirect) {
      emit('confirm', selectedValue.value, reasonText.value.trim())
      emit('update:modelValue', selectedValue.value)
      emit('close')
    } else {
      router.push(selectedValue.value)
    }
  }
}
</script>

<style>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Estilos responsive para el select */
select {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

select option {
  padding: 8px;
  font-size: 14px;
}

@media (max-width: 640px) {
  select {
    font-size: 14px;
    padding: 6px;
  }

  select option {
    font-size: 14px;
    padding: 6px;
  }
}
</style>
